import PermissionCustomerModelController from "../controller/model/PermissionCustomerModelController";
import { getPermissionControllers, hasCustomerPermission } from "../utils/permissionHelper";
import { syncWalletsAfterNotification } from "../utils/walletSync";
import rsaController from "../utils/RSA-controller";
import { clearHomePageInfosClientCache, deleteFcmTokenOnLogout } from "../utils/apiClient";
import { openSupportChatOnCurrentPage } from "../utils/supportChatNavigation";
import { resetUiPreferencesToDefaults } from "../utils/userPreferences";
import { playNotificationRingtone } from "../utils/notificationSound";

export const MFG_FCM_NAV_TARGET_KEY = "mfg_fcm_nav_target";
export const MFG_FCM_NAV_TX_KEY = "mfg_fcm_nav_transaction_id";
export const MFG_FCM_NAV_TRADING_ACCOUNT_ID_KEY = "mfg_fcm_nav_trading_account_id";
export const MFG_FCM_NEW_DEVICE_ID_KEY = "mfg_fcm_new_device_id";
export const MFG_FCM_NEW_DEVICE_MODEL_KEY = "mfg_fcm_new_device_model";

function readUserFromStorage() {
  try {
    const raw = sessionStorage.getItem("userData") || localStorage.getItem("userData");
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}

function canViewHistory(user) {
  const { customerPerm, customerDevicePerm } = getPermissionControllers(user);
  return hasCustomerPermission(
    customerPerm,
    customerDevicePerm,
    PermissionCustomerModelController.PER__CAN_VIEW_HISTORY
  );
}

function canSendChat(user) {
  const { customerPerm, customerDevicePerm } = getPermissionControllers(user);
  return hasCustomerPermission(
    customerPerm,
    customerDevicePerm,
    PermissionCustomerModelController.PER__SEND_MESSAGE
  );
}

function isLoggedIn() {
  const t = localStorage.getItem("token") || sessionStorage.getItem("token");
  return !!(t && t !== "FIRST_TIME_LOGIN" && String(t).trim() !== "");
}

async function forceRelogin(navigate) {
  const username =
    localStorage.getItem("userEmail") || sessionStorage.getItem("userEmail") || null;
  try {
    await deleteFcmTokenOnLogout(username);
  } catch (_) {}
  try {
    clearHomePageInfosClientCache();
  } catch (_) {}
  localStorage.removeItem("token");
  sessionStorage.removeItem("token");
  localStorage.removeItem("userId");
  sessionStorage.removeItem("userId");
  localStorage.removeItem("isLoggedIn");
  sessionStorage.removeItem("isLoggedIn");
  localStorage.removeItem("userEmail");
  sessionStorage.removeItem("userEmail");
  resetUiPreferencesToDefaults();
  try {
    rsaController.clearUserKeys();
  } catch (_) {}
  if (typeof navigate === "function") {
    navigate("/home", { replace: true });
  }
  window.location.reload();
}

function setTxNavigation(target, transactionId) {
  sessionStorage.setItem(MFG_FCM_NAV_TARGET_KEY, target);
  sessionStorage.setItem(MFG_FCM_NAV_TX_KEY, String(transactionId));
}

/**
 * Normalize Firebase JS / FCM payload to the shape Flutter's handlerMessage expects:
 * message.title = logical type (e.g. cashin_money_done), message.data = JSON string of payload map.
 */
export function parseWebFcmPayload(payload) {
  const d = payload?.data && typeof payload.data === "object" ? { ...payload.data } : {};
  const note = payload?.notification || {};
  let logicalTitle = d.title != null && d.title !== "" ? String(d.title) : "";
  if (!logicalTitle && note.title) {
    try {
      const parsed = JSON.parse(note.title);
      if (parsed && typeof parsed === "object" && parsed.title) logicalTitle = String(parsed.title);
    } catch (_) {
      logicalTitle = String(note.title);
    }
  }
  let body = note.body ? String(note.body) : "";
  let inner = {};
  if (d.data != null) {
    if (typeof d.data === "string") {
      try {
        inner = JSON.parse(d.data);
      } catch (_) {
        inner = {};
      }
    } else if (typeof d.data === "object") {
      inner = { ...d.data };
    }
  }
  return {
    title: logicalTitle,
    body,
    dataMap: inner && typeof inner === "object" ? inner : {},
    rawData: d,
  };
}

/** True when FCM payload is a support-chat message (not a global app toast). */
export function isChatFcmPayload(payload) {
  const parsed = parseWebFcmPayload(payload);
  if (parsed.title === "receive_new_message") return true;

  const noteTitle = String(payload?.notification?.title || "").trim().toLowerCase();
  return noteTitle === "new message" || noteTitle.includes("message");
}

/** Notify SupportChatWidget instead of the global toast/header preview. */
export function emitChatIncomingNotification(payload) {
  if (typeof window === "undefined") return;
  try {
    const parsed = parseWebFcmPayload(payload);
    const note = payload?.notification || {};
    const preview =
      note.body != null && String(note.body).trim() !== ""
        ? String(note.body)
        : parsed.body || "";
    const title =
      note.title != null && String(note.title).trim() !== ""
        ? String(note.title)
        : "New Message";

    const chatId =
      parsed.dataMap?.CHAT_ID ??
      parsed.dataMap?.chat_id ??
      parsed.rawData?.CHAT_ID ??
      parsed.rawData?.chat_id ??
      null;

    window.dispatchEvent(
      new CustomEvent("mfg:chat-incoming-message", {
        detail: {
          title,
          preview,
          chatId: chatId != null && String(chatId).trim() !== "" ? String(chatId) : null,
          at: Date.now(),
        },
      })
    );
  } catch (_) {}
}

export function emitHeaderNotificationPreview(payload) {
  if (typeof window === "undefined" || isChatFcmPayload(payload)) return;
  try {
    const parsed = parseWebFcmPayload(payload);
    const note = payload?.notification || {};
    const noteTitle = note.title != null && String(note.title).trim() !== "" ? String(note.title) : "";
    const noteBody = note.body != null ? String(note.body) : "";
    const displayTitle = noteTitle || parsed.title || "Notification";
    const displayBody = noteBody || parsed.body || "";
    window.dispatchEvent(
      new CustomEvent("mfg:header-notification", {
        detail: { title: displayTitle, body: displayBody, at: Date.now() },
      })
    );
  } catch (_) {}
}

/**
 * Mirrors Flutter firebase_messaging_helper.handlerMessage (navigation + side effects).
 * @param {import('react-router-dom').NavigateFunction} navigate
 * @param {object} payload - Firebase onMessage / SW payload
 * @param {boolean} isInternal - true when app is in foreground (same as Flutter)
 */
export async function handleFcmNotificationMessage(navigate, payload, isInternal) {
  if (!isLoggedIn()) return;

  // Any notification (including chat) plays ringtone while the app is active.
  playNotificationRingtone();

  emitHeaderNotificationPreview(payload);

  const { title, body, dataMap } = parseWebFcmPayload(payload);
  if (!title) return;

  const user = readUserFromStorage();

  const runWalletUpdate = async (notificationTitle = "") => {
    await syncWalletsAfterNotification({ title: notificationTitle });
  };

  try {
    if (title === "new_login") {
      if (!dataMap.ID || !dataMap.DEVICE_MODEL) return;
      try {
        sessionStorage.setItem(MFG_FCM_NEW_DEVICE_ID_KEY, String(dataMap.ID));
        sessionStorage.setItem(MFG_FCM_NEW_DEVICE_MODEL_KEY, String(dataMap.DEVICE_MODEL));
      } catch (_) {}
      if (typeof navigate === "function") {
        navigate("/pages/settings/active-devices");
      }
      await runWalletUpdate(title);
      return;
    }

    const transactionHistoryTitles = [
      "cashin_money_done",
      "cashin_money_pending",
      "cashout_money_done",
      "withdrawal_money_rejected",
      "transfer_money_done",
      "transfer_money_pending",
      "refund_money_done",
      "refund_money_pending",
      "refund_money_cancel",
      "refund_money_rejected",
      "transaction_money_accepted",
      "transaction_money_rejected",
      "admin_transfer_money",
    ];

    if (transactionHistoryTitles.includes(title)) {
      if (isInternal) {
        await runWalletUpdate(title);
        return;
      }
      if (!canViewHistory(user)) return;
      if (!dataMap.TRANSACTION_ID) return;
      const usePendingList = title === "refund_money_pending";
      setTxNavigation(usePendingList ? "pending" : "history", dataMap.TRANSACTION_ID);
      if (typeof navigate === "function") {
        navigate(usePendingList ? "/pages/transactions/pending" : "/pages/transactions/history");
      }
      await runWalletUpdate(title);
      return;
    }

    if (title === "cash_out_request") {
      if (isInternal) {
        await runWalletUpdate(title);
        return;
      }
      if (!canViewHistory(user)) return;
      if (dataMap.TRANSACTION_ID == null) return;
      setTxNavigation("cashout_requests", dataMap.TRANSACTION_ID);
      if (typeof navigate === "function") navigate("/pages/transactions/cash-out-requests");
      await runWalletUpdate(title);
      return;
    }

    if (title === "cashout_rejected") {
      if (isInternal) await runWalletUpdate(title);
      return;
    }

    if (title === "exchange_money_done") {
      if (!isInternal && canViewHistory(user) && typeof navigate === "function") {
        const sellId = dataMap.TRANSACTION_SELL;
        const buyId = dataMap.TRANSACTION_BUY;
        if (sellId != null && buyId != null) {
          const goSell =
            typeof window !== "undefined" &&
            window.confirm("Open sell transaction details?");
          if (goSell) {
            setTxNavigation("history", sellId);
            navigate("/pages/transactions/history");
            await runWalletUpdate(title);
            return;
          }
          const goBuy = typeof window !== "undefined" && window.confirm("Open buy transaction details?");
          if (goBuy) {
            setTxNavigation("history", buyId);
            navigate("/pages/transactions/history");
          }
        }
      }
      await runWalletUpdate(title);
      return;
    }

    if (title === "change_password") {
      if (isInternal) {
        if (!dataMap.TOKEN) return;
        const current =
          localStorage.getItem("token") || sessionStorage.getItem("token") || "";
        if (String(dataMap.TOKEN) !== String(current)) {
          await forceRelogin(navigate);
        }
      }
      await runWalletUpdate(title);
      return;
    }

    if (title === "reset_password" || title === "permission_changed") {
      if (isInternal) await forceRelogin(navigate);
      await runWalletUpdate(title);
      return;
    }

    if (title === "receive_new_message") {
      if (isInternal) {
        emitChatIncomingNotification(payload);
        await runWalletUpdate(title);
        return;
      }
      if (!canSendChat(user)) return;
      if (typeof navigate === "function") {
        try {
          openSupportChatOnCurrentPage(
            navigate,
            window.location.pathname,
            window.location.search || ""
          );
        } catch (_) {}
      }
      await runWalletUpdate(title);
      return;
    }

    if (title === "cashout_accepted") {
      if (
        !isInternal &&
        canViewHistory(user) &&
        dataMap.TRANSACTION_ID != null &&
        typeof navigate === "function"
      ) {
        setTxNavigation("cashout_requests", dataMap.TRANSACTION_ID);
        navigate("/pages/transactions/cash-out-requests");
      }
      await runWalletUpdate(title);
      return;
    }

    if (title === "update_trading_account") {
      if (!dataMap.ID) return;
      try {
        sessionStorage.setItem(MFG_FCM_NAV_TRADING_ACCOUNT_ID_KEY, String(dataMap.ID));
      } catch (_) {}
      if (!isInternal && typeof navigate === "function") {
        navigate("/pages/accounts/list");
      }
      await runWalletUpdate(title);
      return;
    }

    if (title === "kyc_ok" || title === "kyc_no") {
      const forceLogout =
        dataMap?.force_logout === "1" ||
        dataMap?.force_logout === 1 ||
        dataMap?.force_logout === true ||
        dataMap?.force_logout === "true";
      if (forceLogout) {
        await forceRelogin(navigate);
        return;
      }
      await runWalletUpdate(title);
      if (title === "kyc_no" && typeof navigate === "function") {
        navigate("/pages/profile-information");
      }
      return;
    }

    const fallback =
      body ||
      (typeof dataMap.data === "string" ? dataMap.data : "") ||
      "Notification";
    if (typeof window !== "undefined") {
      window.alert(`${title}\n\n${fallback}`);
    }
    await runWalletUpdate(title);
  } catch (_) {
    await runWalletUpdate(title);
  }
}
